import { useRef, useCallback } from 'react';

/**
 * Scroll-to-Top Hook for Tab Navigation
 * Scrolls to top when tapping the same tab again (like native apps)
 * 
 * Usage:
 * const { handleTabSelect, currentTab } = useScrollToTopOnTab(0); // 0 = initial tab
 * 
 * <Tabs selectedIndex={currentTab} onSelect={handleTabSelect}>
 *   <TabPanel>
 *     <div ref={scrollRefs[0]}>...</div>
 *   </TabPanel>
 * </Tabs>
 */
export const useScrollToTopOnTab = (initialTab: number = 0) => {
    const currentTab = useRef(initialTab);
    const scrollContainers = useRef<Map<number, HTMLElement>>(new Map());

    const registerScrollContainer = useCallback((tabIndex: number, element: HTMLElement | null) => {
        if (element) {
            scrollContainers.current.set(tabIndex, element);
        } else {
            scrollContainers.current.delete(tabIndex);
        }
    }, []);

    const scrollToTop = useCallback((tabIndex: number) => {
        const container = scrollContainers.current.get(tabIndex);
        if (container) {
            container.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        } else {
            // Fallback to window scroll
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, []);

    const handleTabSelect = useCallback((tabIndex: number) => {
        // If tapping the same tab, scroll to top
        if (tabIndex === currentTab.current) {
            scrollToTop(tabIndex);
        }
        
        currentTab.current = tabIndex;
        return tabIndex;
    }, [scrollToTop]);

    return {
        handleTabSelect,
        registerScrollContainer,
        currentTab: currentTab.current,
    };
};
